import { Note, Scale } from 'tonal';
import { chromaOf } from './fretboard';
import {
  chordFromNumeral,
  chordSymbol,
  diatonicChords,
  hasMinorThird,
  isSeventh,
  keyName,
  minorKey,
  parallelKey,
  parseChordSymbol,
  romanOf,
  sameChord,
  type ChordQuality,
  type ChordSpec,
  type DiatonicChord,
  type KeyMode,
  type MusicalKey,
  type ScaleDegree,
} from './harmony';

/**
 * Minor-key harmony and chord function (topics I4, I5).
 *
 * - Minor keys are natural minor (as in `harmony.ts`) plus the harmonic-minor
 *   V, V7, vii° and vii°7. The raised 7th is the leading tone, so E7 *is* in
 *   A minor (research/03 I4 misconception).
 * - Functions are the three classes tonic, subdominant (predominant) and
 *   dominant. Major: I iii vi tonic, ii IV subdominant, V vii° dominant.
 *   Minor: i bIII tonic, ii° iv bVI subdominant, v V bVII vii° dominant.
 * - A chromatic chord can have several readings (D in A minor is the borrowed
 *   IV and V/bVII). `chordReadings` returns them all, preferred first: a
 *   dominant 7th reads as a secondary dominant before a borrowed chord (the
 *   7th makes the pull explicit); a plain triad reads as borrowed first.
 *   Drills can accept any reading, or skip chords with more than one.
 * - Numerals follow `harmony.ts`: measured against the major scale on the
 *   tonic, so borrowed chords in C read bIII, iv, bVI, bVII.
 */

export type HarmonicFunction = 'tonic' | 'subdominant' | 'dominant';

export type ChordRole =
  'diatonic' | 'harmonic-minor' | 'secondary-dominant' | 'borrowed' | 'tritone-substitute';

                                                                                                    

                                                                 
                                                                                           
                                                                                                 
  

/** Collapse double accidentals (Ebb → D) and leave everything else alone. */
                                     
                                                         
 

                                                       
                                                                     
 

/** The function of a diatonic degree: `diatonicFunction('minor', 6)` → subdominant. */
export function diatonicFunction(mode: KeyMode, degree: ScaleDegree): HarmonicFunction {
  return FUNCTIONS[mode][degree - 1] as HarmonicFunction;
}

// ---------------------------------------------------------------------------
// Minor-key harmony (I4)

/** A harmonic minor: A B C D E F G#. */
                                                             
                                                           
                                                                     
               
 

/** The natural and raised 7th of a minor key: A minor → G and G#. */
                                                                                   
                              
          
                                             
                                            
    
 

/** The note a half step below the tonic that pulls up to it (raised 7th in minor). */
                                                      
                                         
 

export interface MinorKeyChord extends DiatonicChord {
  /** `natural` for the natural-minor chords, `harmonic` for V and vii° from harmonic minor. */
  readonly source: 'natural' | 'harmonic';
}

/** The harmonic-minor chords a minor key adds: V and vii° (V7 and vii°7 with `sevenths`). */
                                    
                
                                    
                    
                              
                                                                   
                                                       
 

/**
 * The working chords of a minor key, by degree: natural minor plus the
 * harmonic-minor V and vii° (A minor: Am B° C Dm Em E F G G#°).
 */
                                                                                                   
                                                                     
         
                               
      
                                                                 
         
                                
      
                                                                       
 

/**
 * ii–V–I: Dm7 G7 Cmaj7 in C major; iiø7 V7 i (Bm7b5 E7 Am) in A minor. With
 * `sevenths: false`, triads (ii° V i in minor).
 */
                                                                                                 
                                         
                  
                        
                
                                
                          
                
                             
                            
                                                       
 

// ---------------------------------------------------------------------------
// Chord function and substitution (I5)

export interface ChordReading {
  readonly role: ChordRole;
  readonly function: HarmonicFunction;
  /** The chord, spelled for the key. */
  readonly chord: ChordSpec;
  readonly symbol: string;
  /** Roman numeral or functional label: `ii`, `V7`, `V7/ii`, `bVII`, `subV7/V`. */
  readonly label: string;
  /** What a secondary dominant or tritone substitute resolves to, e.g. `ii`; else null. */
  readonly target: string | null;
  /** The key a borrowed chord comes from; else null. */
  readonly source: MusicalKey | null;
  /** One sentence for feedback. */
  readonly explanation: string;
}

interface Target {
  readonly chord: DiatonicChord;
  /** The dominant that resolves to it, a 5th above. */
  readonly dominantRoot: string;
}

/**
 * Chords that can be tonicised: the major or minor diatonic triads other than
 * the tonic (minor keys use the harmonic V, so V/V is B7 in A minor).
 */
                                                      
                                                                                        
               
                                                                
                                                                                      
                                                                                       
 

                                                     
                                                                 
 

                                                                       
                                                             
 

/** The secondary dominants of a key: V/ii … V/vi in major (V7/… with `sevenths`). */
                                   
                  
                                    
                   
                                                                      
                                         
                                                                      
    
 

                                                                                                   
                                                           
                                    
          
                               
                         
          
           
          
                         
                 
                                                                                               
    
 

/**
 * The tritone substitute of a dominant 7th: the dominant 7th a tritone away,
 * sharing its 3rd and 7th (G7 → Db7). Spelled without Cb, Fb, E#, B# or
 * double accidentals (F7 → B7).
 */
                                                                         
                          
                                
                                                                         
                                            
          
                                                                      
                         
    
 

/** Tritone substitutes in a key: the dominant 7th a half step above each target (and the tonic). */
                                                           
                                                                                  
                             
                                                                                                
                                      
                                                                
                                  
                                               
                           
       
            
                                          
                                    
            
             
            
                      
                   
                                                                                                                                      
      
     
 

                                                                            
                                           
 

/**
 * Chords borrowed from the parallel key (modal interchange): in C major, Cm
 * D° Eb Fm Gm Ab Bb (sevenths with `sevenths`); in a minor key, the parallel
 * major's chords other than the harmonic-minor V and vii°.
 */
                                                                                                    
                                          
                                  
                                       
                                       
                                                     
                 
                                    
                                    
              
                                  
                                                          
                                                    
               
              
                     
               
                                                                                                   
        
       
 

/** The key's own chords: diatonic, plus the harmonic-minor ones in a minor key. */
                                                                         
                                           
                                                                                                  
 

                                                                         
                                                                                       
                
                                                          
            
                       
                   
                                                              
                             
                           
                   
                   
                                                                                                                     
      
   
                                        
                                                                                                 
                    
    
                             
                                                                 
          
                           
                         
                                                              
                            
                          
                 
                 
                                                                                                                                                                                                         
    
 

/**
 * Every reading of a chord in a key, preferred first. A diatonic or
 * harmonic-minor chord has exactly one; a chromatic chord may have several;
 * an unexplained chord (bII in a minor key, say) has none.
 */
                                                                                           
                          
                                 
                        
                                         
            
            
                                                               
                                                      
     
                                                                                              
                                                                                        
                          
    
                 
                                                                                               
                 
                             
                                               
                                    
                 
 

/** The preferred reading, or `null` for a chord this module can't explain. */
                                                                                               
                                              
 

/** The chord's function in the key (preferred reading), or `null`. */
                                                                                                 
                                                    
 

/** True when some reading of the chord is a borrowed chord. */
                                                                                 
                                                                      
 

/** "Spot the borrowed chord": indexes of the borrowed chords in a progression. */
                                                                                                  
                                                                   
 

// ---------------------------------------------------------------------------
// Substitutes

export type SubstituteKind = 'relative' | 'same-function' | 'borrowed' | 'tritone';

export interface Substitute {
  readonly kind: SubstituteKind;
  readonly chord: ChordSpec;
  readonly symbol: string;
  /** Numeral in the key. */
  readonly label: string;
}

                                                                       
                 
                 
                   
                      
                   
  

/**
 * The relative chord, which shares two notes: C ↔ Am, F ↔ Dm, G7 → Em7,
 * Am7 → Cmaj7. `null` for diminished and augmented chords.
 */
                                                                                 
                          
                                              
                                         
                                                                                     
                           
 

/**
 * Substitutes for a chord in a key ("pick a substitute"): its relative, the
 * diatonic chords with the same function, the parallel key's chord on the
 * same degree (diatonic chords only), and the tritone substitute (dominant
 * 7ths). Each keeps its own spelling: the parallel key's (Eb in C), the
 * relative's (Am for C), the tritone substitute's (Db7 for G7).
 */
                                                                                       
                          
                                       
                               
                                                       
                                                                          
                                                      
                                                                                          
    
                                         
                                          
                                     
                                          
                                                                                                   
                                                  
                                                                                             
     
                                                                                           
                              
   
                                                                      
             
 

import { FUNCTIONS } from "/Users/pookey/Documents/personal/Projects.nosync.noindex/guitar.nosync/app/src/theory/function.ts?split=1";
